import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  Button,
  Typography,
  Box,
  Divider,
  IconButton,
  Chip,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import "dayjs/locale/fr"; // Import French locale
import "dayjs/locale/en"; // Import English locale

function RequestPendingModal({
  open,
  onClose,
  pendingRequests,
  onApprove,
  onReject,
  departments,
}) {
  const { t, i18n } = useTranslation();
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedDepartments, setSelectedDepartments] = useState({});

  // Set dayjs locale based on current language
  dayjs.locale(i18n.language);

  const statusTypes = {
    all: { color: "default", label: t("All") },
    Pending: { color: "warning", label: t("Pending") },
    Approved: { color: "success", label: t("Approved") },
    Rejected: { color: "error", label: t("Rejected") },
    PendingFranchisee: {
      color: "info",
      label: t("Pending Franchisee Approval"),
    },
  };

  const getStatusChip = (status) => {
    const { color, label } = statusTypes[status] || statusTypes.Pending;
    return <Chip label={label} color={color} size="small" />;
  };

  const formatDate = (date) => {
    return dayjs(date).format("DD/MM/YYYY HH:mm");
  };

  const filteredRequests =
    statusFilter === "all"
      ? pendingRequests
      : pendingRequests?.filter((request) => request.status === statusFilter);

  const handleStatusFilterChange = (event, newFilter) => {
    if (newFilter !== null) {
      setStatusFilter(newFilter);
    }
  };

  const handleDepartmentChange = (requestId, department) => {
    setSelectedDepartments((prev) => ({
      ...prev,
      [requestId]: department,
    }));
  };

  const handleApprove = (requestId) => {
    const department = selectedDepartments[requestId];
    if (!department) {
      // Show error or alert that department is required
      return;
    }
    onApprove(requestId, department);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {t("Pending Approvals")}
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        {/* Status Filter */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t("Filter by Status")}:
          </Typography>
          <ToggleButtonGroup
            value={statusFilter}
            exclusive
            onChange={handleStatusFilterChange}
            aria-label="status filter"
            size="small"
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              "& .MuiToggleButton-root": {
                border: "1px solid rgba(0, 0, 0, 0.12)",
                borderRadius: "16px !important",
                px: 2,
              },
            }}
          >
            {Object.entries(statusTypes).map(([status, { color, label }]) => (
              <ToggleButton
                key={status}
                value={status}
                sx={{
                  "&.Mui-selected": {
                    backgroundColor: `${color}.main`,
                    color: "white",
                    "&:hover": {
                      backgroundColor: `${color}.dark`,
                    },
                  },
                }}
              >
                {label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {filteredRequests?.length === 0 ? (
          <Typography align="center" sx={{ py: 3 }}>
            {t("No requests found")}
          </Typography>
        ) : (
          <List sx={{ gap: 2, display: "flex", flexDirection: "column" }}>
            {filteredRequests?.map((request) => (
              <Paper
                key={request.id}
                elevation={3}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: "background.paper",
                  "&:hover": {
                    bgcolor: "background.default",
                  },
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    {request.companyName}
                  </Typography>
                  {getStatusChip(request.status)}
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    mb: 2,
                    "& > *": {
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                      bgcolor: "background.default",
                    },
                  }}
                >
                  {request.status === "PendingFranchisee" && (
                    <Typography variant="body2">
                      <strong>{t("Employee Name")}:</strong>{" "}
                      {request.employeeFirstName} {request.employeeLastName}
                    </Typography>
                  )}

                  <Typography variant="body2">
                    <strong>{t("Employee Email")}:</strong>{" "}
                    {request.employeeEmail}
                  </Typography>

                  <Typography variant="body2">
                    <strong>{t("Created by")}:</strong> {request.createdByName}
                  </Typography>

                  <Typography variant="body2">
                    <strong>{t("Created")}:</strong>{" "}
                    {formatDate(request.dateCreation)}
                  </Typography>

                  {request.dateModification && (
                    <Typography variant="body2">
                      <strong>{t("Modified")}:</strong>{" "}
                      {formatDate(request.dateModification)}
                    </Typography>
                  )}

                  {request.status === "PendingFranchisee" && (
                    <Box sx={{ mt: 2 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel id={`department-label-${request.id}`}>
                          {t("Select Department")}
                        </InputLabel>
                        <Select
                          labelId={`department-label-${request.id}`}
                          value={selectedDepartments[request.id] || ""}
                          onChange={(e) =>
                            handleDepartmentChange(request.id, e.target.value)
                          }
                          label={t("Select Department")}
                          required
                        >
                          {departments?.map((dept) => (
                            <MenuItem key={dept.id} value={dept.id}>
                              {dept.textEn}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                </Box>

                {request.status === "PendingFranchisee" && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 2,
                      mt: 2,
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<CheckIcon />}
                      onClick={() => handleApprove(request.id)}
                      disabled={!selectedDepartments[request.id]}
                      sx={{ px: 3 }}
                    >
                      {t("Approve")}
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<CloseIcon />}
                      onClick={() => onReject(request.id)}
                      sx={{ px: 3 }}
                    >
                      {t("Reject")}
                    </Button>
                  </Box>
                )}
              </Paper>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default RequestPendingModal;
